import { Box, Container, Fade, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import FixedNavigationBar from '@/components/common/fixed-top-bar';
import { useGetEventQuery } from '@/state/queries/events/use-get-event';
import { useEventStore } from '@/state/stores/use-event-store';
import { useTabs } from '@/hooks/use-tabs';
import { useTranslations } from '@/i18n/use-translations';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import { SpaceConfigurationTab } from './space-configuration-tab';

const tabs = ['configuration'] as const;

export default function SpaceDetail() {
  const t = useTranslations();
  const { eventId, spaceId } = useParams();
  const navigate = useNavigate();
  const isMobile = useBreakpointDown('sm');
  const { tabValue, tabIndex, handleTabChange } = useTabs(tabs);
  const { data: event, normalized } = useGetEventQuery(eventId);
  const setEvent = useEventStore((s) => s.setEvent);
  const setEventPlan = useEventStore((s) => s.setEventPlan);
  const setEventOrganize = useEventStore((s) => s.setEventOrganize);

  useEffect(() => {
    if (!event) return;
    if (normalized?.eventPlan) {
      setEventPlan(normalized.eventPlan);
    }
    if (normalized?.eventOrganize) {
      setEventOrganize(normalized.eventOrganize);
    }
    setEvent(event);
  }, [event, normalized, setEvent, setEventPlan, setEventOrganize]);

  const space = event?.spaces?.find((item) => item.id === spaceId);

  useEffect(() => {
    if (event && spaceId && !space && eventId) {
      navigate(`/event/${eventId}`, { replace: true });
    }
  }, [event, space, spaceId, eventId, navigate]);

  if (!eventId || !spaceId) return null;

  return (
    <Container
      sx={{
        'borderBottom': 0,
        'borderColor': 'divider',
        'minHeight': '100vh',
        '@supports (height: 100dvh)': {
          minHeight: '100dvh',
        },
      }}
    >
      <FixedNavigationBar context="event" backTo={`/event/${eventId}`} showBackButton={!isMobile}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'scrollable'}
          scrollButtons={isMobile ? false : 'auto'}
          sx={{
            'width': isMobile ? '100%' : 'calc(100% + 32px)',
            'mx': isMobile ? 0 : -2,
            'px': isMobile ? 0 : 2,
            '& .MuiTab-root': {
              'minWidth': 'fit-content',
              '&:focus': { outline: 'none' },
              '&.Mui-focusVisible': { outline: 'none' },
            },
          }}
        >
          {tabs.map((tabOption) => (
            <Tab key={tabOption} label={t(`event.detail.space.${tabOption}.title`)} />
          ))}
        </Tabs>
      </FixedNavigationBar>
      <Stack
        sx={{
          mt: 'calc(var(--fixed-navigation-bar-height) - (var(--fixed-topbar-visible) * var(--fixed-topbar-min-height)))',
        }}
        justifyContent="center"
        alignItems="center"
      >
        <Fade in timeout={500}>
          <Box width="100%" sx={{ m: '0.8em 0' }}>
            {space ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {space.name}
                </Typography>
                {tabValue === 'configuration' ? <SpaceConfigurationTab spaceId={spaceId} eventId={eventId} /> : null}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('event.detail.space.notFound')}
              </Typography>
            )}
          </Box>
        </Fade>
      </Stack>
    </Container>
  );
}
